import React, { useState } from "react"
import { graphql } from "gatsby"

import { useTranslation } from "@zestia/gatsby-plugin-react-i18next"
import findImage from "@/utils/find-image"

import MainLayout from "@/layouts/MainLayout"

import PPCHeader from "@/components/PPCHeader"
import ComparisonAwards from "@/components/ComparisonAwards"
import CustomerRatingPanel from "@/components/CustomerRatingPanel"
import CustomerRating from "@/components/CustomerRating"
import SplitPane from "@/components/SplitPane"
import GatedContentForm from "@/components/GatedContentForm"
import QuoteLarge from "@/components/Quote"
import PricingSnapshot from "@/components/PricingSnapshot"

import outlookCalendarVideo from "@/videos/outlook-calendar-integration.mp4"
import contactHistoryVideo from "@/videos/contact-history.mp4"
import salesAnalyticsVideo from "@/videos/sales-analytics-reports.mp4"

import SharedHead from "@/components/SharedHead"
import useHeadTranslation from "@/hooks/use-head-translation"
import SignUpModal from "@/components/SignUpModal"

export const Head = props => {
  const { t } = useHeadTranslation(props)

  return (
    <SharedHead
      {...props}
      title={t("outlook-crm.title")}
      description={t("outlook-crm.description")}
      noIndex={true}
    />
  )
}

const OutlookCrmPage = ({ location, data }) => {
  const { t } = useTranslation()

  const [tier, setTier] = useState()
  const [frequency, setFrequency] = useState("ANNUAL")

  const headerBenefits = Object.values(
    t("outlook-crm.header.benefits.list", { returnObjects: true }),
  )

  return (
    <MainLayout hideFooterLinks={true}>
      <PPCHeader
        title={t("outlook-crm.header.title")}
        titleClass={"h1--xl"}
        description={t("outlook-crm.header.body")}
        primaryButtonAction={
          <button className="btn " onClick={() => setTier("growth")}>
            {t("outlook-crm.header.button")}
          </button>
        }
        image={{
          src: findImage(data.allFile, "hero-projects"),
          alt: t("outlook-crm.header.image-alt"),
        }}
        benefitsTitle={t("outlook-crm.header.benefits.title")}
        benefitsItems={headerBenefits}
      />

      <CustomerRatingPanel>
        <CustomerRating
          comment={t("outlook-crm.reviews.1.review")}
          cite={t("outlook-crm.reviews.1.rating")}
          rating={4.5}
        />
        <CustomerRating
          comment={t("outlook-crm.reviews.2.review")}
          cite={t("outlook-crm.reviews.2.rating")}
          rating={4.5}
        />
        <CustomerRating
          comment={t("outlook-crm.reviews.3.review")}
          cite={t("outlook-crm.reviews.3.rating")}
          rating={4.6}
        />
      </CustomerRatingPanel>

      <ComparisonAwards />
      <SplitPane
        assetAlignment="left"
        video={outlookCalendarVideo}
        title={t("outlook-crm.connect-microsoft.title")}
        text={t("outlook-crm.connect-microsoft.text")}
      />
      <SplitPane
        assetAlignment="right"
        video={contactHistoryVideo}
        title={t("outlook-crm.goodbye-spreadsheets.title")}
        text={t("outlook-crm.goodbye-spreadsheets.text")}
      />
      <GatedContentForm
        title={t("crm-guide.header.title")}
        text={t("crm-guide.header.text")}
        centered="true"
        backgroundClass="text-white"
        titleClass="h2 text-white"
        checklistItems={[
          t("crm-guide.header.checklist.one"),
          t("crm-guide.header.checklist.two"),
          t("crm-guide.header.checklist.three"),
          t("crm-guide.header.checklist.four"),
        ]}
        formUrl={"https://api.transpond.io/form?am=30494&fid=16552"}
        formTitle={t("crm-guide.header.form-title")}
        formId={"freeGuideDownload"}
      />
      <div className="mt-16">
        <QuoteLarge
          quote={t("outlook-crm.quote.text")}
          cite={{
            name: "Tere Mathern",
            role_company: "Capacity Building Partnerships",
          }}
        />
      </div>
      <SplitPane
        assetAlignment="left"
        video={salesAnalyticsVideo}
        title={t("outlook-crm.bigger-picture.title")}
        text={t("outlook-crm.bigger-picture.text")}
      />
      <div className="mb-20 pb-20 bg-grey-1">
        <PricingSnapshot
          frequency={frequency}
          onSelectPlan={setTier}
          onTogglePlanFrequency={setFrequency}
          showFrequencyToggle={true}
          tier={tier}
          location={location}
          closeModal={() => setTier(null)}
        />
      </div>

      {tier && (
        <SignUpModal
          tier={tier}
          variation="FrictionlessModal"
          title={t(`signup.modal-title.${tier}`)}
          location={location}
          frequency={frequency}
          closeModal={() => setTier(null)}
        />
      )}
    </MainLayout>
  )
}

export const query = graphql`
  query {
    ...Locales
    allFile(filter: { sourceInstanceName: { eq: "images" } }) {
      edges {
        node {
          childImageSharp {
            gatsbyImageData
          }
        }
      }
    }
  }
`

export default OutlookCrmPage
